import { createContext, useContext, useEffect, useMemo, useState } from "react";

const QuoteContext = createContext(null);

function load(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function QuoteProvider({ children }) {
  // Only non-sensitive selections live in local storage — never contact details.
  const [items, setItems] = useState(() => load("mv_quote_v1", []));
  const [palette, setPalette] = useState(() => load("mv_atelier_v1", null));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [atelierOpen, setAtelierOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem("mv_quote_v1", JSON.stringify(items));
    } catch {}
  }, [items]);

  useEffect(() => {
    try {
      window.localStorage.setItem("mv_atelier_v1", JSON.stringify(palette));
    } catch {}
  }, [palette]);

  const value = useMemo(() => {
    const count = items.reduce((n, i) => n + (i.quantity || 1), 0);
    return {
      items,
      count,
      palette,
      drawerOpen,
      atelierOpen,
      setDrawerOpen,
      setAtelierOpen,
      addItem: (item, options = "") => {
        setItems((prev) => {
          const found = prev.find((i) => i.slug === item.slug);
          if (found) {
            return prev.map((i) =>
              i.slug === item.slug ? { ...i, quantity: (i.quantity || 1) + 1, options: options || i.options } : i
            );
          }
          return [...prev, { slug: item.slug, title: item.title, image: item.image, unit: item.unit || "", quantity: 1, options, notes: "" }];
        });
      },
      removeItem: (slug) => setItems((prev) => prev.filter((i) => i.slug !== slug)),
      setQuantity: (slug, quantity) =>
        setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, quantity: Math.max(1, Math.min(100, quantity)) } : i))),
      setNotes: (slug, notes) => setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, notes } : i))),
      clearQuote: () => setItems([]),
      savePalette: (p) => setPalette(p),
      clearPalette: () => setPalette(null),
    };
  }, [items, palette, drawerOpen, atelierOpen]);

  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>;
}

export const useQuote = () => useContext(QuoteContext);
